// RechnungenTabelle.tsx — die Übersicht des Moduls Zahlungsverkehr.
//
// Ebene: RECHNUNGEN, nicht Positionen. Eine Rechnung ist das, was der Kunde
// bekommt und bezahlt; ihre Positionen sind das Innenleben und gehören ins
// Detail. Die Positions-Arbeitsliste (`OffenePostenTabelle`) bleibt daneben
// bestehen — sie beantwortet die andere Frage („welche einzelne Zeile hängt?")
// und wird im Kontakt-Reiter und im Rechnungs-Detail weiterverwendet.
//
// Spaltenschnitt wie im Access-Original, um den Umstieg nicht zu erzwingen:
// Status · Nr · Versanddatum · Fälligkeit · Name · Kunde gesamt · Kunde offen ·
// Versicherer offen · K-Verantwortlichkeit. Der Status ist das farbige Kästchen
// mit der Zahl; der volle Satztext hängt am Tooltip.

import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { I } from "./kvpIcons";
import { DataTable, Dash, type Column, type SortState } from "./table";
import { StatusBadge, STATUS_CODES, statusText } from "./invoiceStatus";
import { FoBoBadge, type FoBoTeam, type FoBoUser } from "../shared/foBo";
import NeueRechnungDialog from "./NeueRechnungDialog";
import "./contactRedesign.css";
import { apiFetch } from "../../utils/apiFetch";
import { useDebouncedValue } from "../../utils/useDebouncedValue";
import { useInfiniteRows } from "../../hooks/useInfiniteRows";
import { useAuth } from "../../context/AuthContext";
import { formatEuro, formatDate } from "./contractRiskCells";
import { serverMeldung } from "./serverMeldung";

export type RechnungRow = {
  id: number;
  invoiceNbr: string;
  sentDate: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  status: number;
  manuallySettled: boolean;
  creditPaidOut: boolean;
  contactId: number | null;
  kunde: string | null;
  amountCustomer: number | null;
  amountCustomerOpen: number | null;
  amountInsurer: number | null;
  amountInsurerOpen: number | null;
  remark: string | null;
  positionCount: number;
  boTeam: FoBoTeam | null;
  boUser: FoBoUser | null;
  foUser: FoBoUser | null;
};

type Summen = { kundeGesamt: number; kundeOffen: number; versichererOffen: number };

/**
 * Versanddatum mit Rückfall auf das Rechnungsdatum: der aus Access übernommene
 * Bestand kennt nur EIN Datum, neue Rechnungen führen beide getrennt.
 */
const versanddatum = (r: RechnungRow): string | null => r.sentDate ?? r.invoiceDate;

const RechnungenTabelle: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const darfAnlegen = hasPermission("invoices:create");

  const [nurOffen, setNurOffen] = useState(true);
  const [statusFilter, setStatusFilter] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [sort, setSort] = useState<SortState | null>(null);
  const [summen, setSummen] = useState<Summen>({
    kundeGesamt: 0,
    kundeOffen: 0,
    versichererOffen: 0,
  });
  const [fehler, setFehler] = useState<string | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);

  const statusKey = statusFilter.join(",");

  const fetchPage = useCallback(
    async ({ limit, offset, signal }: { limit: number; offset: number; signal: AbortSignal }) => {
      const params = new URLSearchParams({
        offen: String(nurOffen),
        limit: String(limit),
        offset: String(offset),
      });
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      for (const s of statusFilter) params.append("status", String(s));
      if (sort?.field) {
        params.set("sort", sort.field);
        params.set("direction", sort.direction === "asc" ? "ASC" : "DESC");
      }

      try {
        const res = await apiFetch<RechnungRow[]>(`invoices?${params.toString()}`, { signal });
        const headers = res.response?.headers;
        if (headers) {
          setSummen({
            kundeGesamt: Number(headers.get("X-Sum-Kunde-Gesamt") ?? 0),
            kundeOffen: Number(headers.get("X-Sum-Kunde-Offen") ?? 0),
            versichererOffen: Number(headers.get("X-Sum-Versicherer-Offen") ?? 0),
          });
        }
        setFehler(null);
        return { rows: res.data ?? [], total: res.total ?? null };
      } catch (e) {
        // Ein verschluckter Ladefehler sieht aus wie „keine Rechnungen" — und
        // genau das darf eine Geldliste nie behaupten. Die Summen werden
        // zusätzlich genullt: sie stammen sonst noch vom vorherigen Filter und
        // stünden als scheinbar gültige Zahlen unter einer leeren Tabelle.
        if (!signal.aborted) {
          setFehler(serverMeldung(e, "Rechnungen konnten nicht geladen werden."));
          setSummen({ kundeGesamt: 0, kundeOffen: 0, versichererOffen: 0 });
        }
        throw e;
      }
    },
    [nurOffen, debouncedSearch, statusKey, sort],
  );

  const { rows, total, hasMore, loading, loadMore } = useInfiniteRows<RechnungRow>({
    fetchPage,
    getKey: (r) => r.id,
    deps: [nurOffen, debouncedSearch, statusKey, sort],
  });

  const toggleStatus = useCallback((code: number) => {
    setStatusFilter((alt) =>
      alt.includes(code) ? alt.filter((s) => s !== code) : [...alt, code],
    );
  }, []);

  const columns = useMemo<Column<RechnungRow>[]>(
    () => [
      {
        id: "status",
        header: "Status",
        sortable: true,
        sortField: "status",
        width: 78,
        align: "center",
        cell: (r) => <StatusBadge status={r.status} manuellErledigt={r.manuallySettled} />,
      },
      {
        id: "nummer",
        header: "Nr",
        sortable: true,
        sortField: "nummer",
        width: 118,
        cell: (r) => <span className="mono-nr">{r.invoiceNbr}</span>,
      },
      {
        id: "versanddatum",
        header: "Versanddatum",
        sortable: true,
        sortField: "versanddatum",
        width: 128,
        align: "right",
        cell: (r) => {
          const datum = versanddatum(r);
          return datum ? <>{formatDate(datum)}</> : <Dash />;
        },
      },
      {
        id: "faelligkeit",
        header: "Fälligkeit",
        sortable: true,
        sortField: "faelligkeit",
        width: 116,
        align: "right",
        cell: (r) => {
          if (!r.dueDate) return <Dash />;
          // Überfällig heisst: das Geld des Kunden steht noch aus UND der Termin
          // ist durch. Eine Rechnung, bei der nur noch wir dran sind, ist nicht
          // „überfällig" — da schuldet niemand uns etwas.
          const ueberfaellig =
            (r.amountCustomerOpen ?? 0) > 0 &&
            !r.manuallySettled &&
            new Date(r.dueDate) < new Date();
          return (
            <span className={ueberfaellig ? "txt-danger" : undefined}>
              {formatDate(r.dueDate)}
            </span>
          );
        },
      },
      {
        id: "name",
        header: "Name",
        sortable: true,
        sortField: "name",
        minWidth: 190,
        cell: (r) =>
          r.kunde ? (
            <button
              className="lnk"
              onClick={(e) => {
                e.stopPropagation();
                if (r.contactId) navigate(`/contacts/${r.contactId}`);
              }}
            >
              {r.kunde}
            </button>
          ) : (
            <Dash />
          ),
      },
      {
        id: "kundeGesamt",
        header: "Kunde gesamt",
        sortable: true,
        sortField: "kundeGesamt",
        width: 132,
        align: "right",
        cell: (r) => (r.amountCustomer == null ? <Dash /> : <>{formatEuro(r.amountCustomer)}</>),
      },
      {
        id: "kundeOffen",
        header: "Kunde offen",
        sortable: true,
        sortField: "kundeOffen",
        width: 128,
        align: "right",
        cell: (r) =>
          (r.amountCustomerOpen ?? 0) > 0 ? (
            <strong className="txt-danger">{formatEuro(r.amountCustomerOpen)}</strong>
          ) : (
            <span className="txt-muted">{formatEuro(0)}</span>
          ),
      },
      {
        id: "versichererOffen",
        header: "Versicherer offen",
        sortable: true,
        sortField: "versichererOffen",
        width: 148,
        align: "right",
        cell: (r) =>
          (r.amountInsurerOpen ?? 0) > 0 ? (
            <strong className="txt-warn">{formatEuro(r.amountInsurerOpen)}</strong>
          ) : (
            <span className="txt-muted">{formatEuro(0)}</span>
          ),
      },
      {
        id: "verantwortlich",
        header: "K-Verantwortlichkeit",
        width: 176,
        cell: (r) => <FoBoBadge boTeam={r.boTeam} foUser={r.foUser} />,
      },
    ],
    [navigate],
  );

  return (
    <div className="kvp-rd kvp-rd-table-page">
      <div className="bar">
        <div className="search">
          {I.search}
          <input
            placeholder="Kunde oder Rechnungsnummer …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Statusfilter wie die Zahlenreihe im Access-Formular: die Kästchen
            sind selbst die Schalter, Mehrfachauswahl möglich. */}
        <div className="inv-statusfilter" role="group" aria-label="Nach Status filtern">
          {STATUS_CODES.map((code) => (
            <button
              key={code}
              type="button"
              className={statusFilter.includes(code) ? "active" : undefined}
              aria-pressed={statusFilter.includes(code)}
              title={statusText(code)}
              onClick={() => toggleStatus(code)}
            >
              <StatusBadge status={code} />
            </button>
          ))}
        </div>

        <div className="spacer" style={{ flex: 1 }} />

        <div className="rd-seg">
          <button className={nurOffen ? "active" : undefined} onClick={() => setNurOffen(true)}>
            Offen
          </button>
          <button className={!nurOffen ? "active" : undefined} onClick={() => setNurOffen(false)}>
            Alle
          </button>
        </div>

        {total != null && <span className="rd-count">{total} Rechnungen</span>}

        {darfAnlegen && (
          <button className="cd-save" onClick={() => setNeuOffen(true)}>
            Neue Rechnung
          </button>
        )}
      </div>

      {fehler && <div className="rd-error">{fehler}</div>}

      <DataTable<RechnungRow>
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        sort={sort}
        onSortChange={setSort}
        onRowClick={(r) => navigate(`/rechnungen/${r.id}`)}
        onLoadMore={loadMore}
        hasMore={hasMore}
        loading={loading}
        emptyText={
          nurOffen
            ? "Keine offenen Rechnungen — alles ausgeglichen."
            : "Keine Rechnungen gefunden."
        }
      />

      {/* Summenzeile: die DataTable kennt keine Aggregation. Die Werte kommen
          aus den X-Sum-Headern und gelten für die GESAMTE gefilterte Menge —
          nicht nur für die geladenen Zeilen. */}
      <div className="rd-summen">
        <span>
          Kunde gesamt <strong>{formatEuro(summen.kundeGesamt)}</strong>
        </span>
        <span>
          Kunde offen <strong className="txt-danger">{formatEuro(summen.kundeOffen)}</strong>
        </span>
        <span>
          Versicherer offen{" "}
          <strong className="txt-warn">{formatEuro(summen.versichererOffen)}</strong>
        </span>
      </div>

      {neuOffen && (
        <NeueRechnungDialog
          onClose={() => setNeuOffen(false)}
          onAngelegt={(id) => navigate(`/rechnungen/${id}`)}
        />
      )}
    </div>
  );
};

export default RechnungenTabelle;
